import { useMemo, useState } from 'react';
import { HStack, View } from 'native-base';

import { EquipmentInstancesDto } from '@pimm/services/lib/store-equipment';
import { SiteInfo } from '../types';
import { useGetDisplayGroups } from '../hooks/get-display-groups.hook';
import { useGetHardwareConfig } from '../hooks/get-hardware-config.hook';
import { EquipmentChartData } from './equipment-chart-data';
import { EquipmentList } from './equipment-list';

type EquipmentStatusProps = {
  queryDisplayGroups: ReturnType<typeof useGetDisplayGroups>;
  siteId?: string;
  initialInstanceId?: string;
  onChangeInstance?: (instanceId?: string) => void;
  siteInfo?: SiteInfo;
  generatedBy?: string;
};

export const EquipmentStatus = ({
  queryDisplayGroups,
  siteId,
  initialInstanceId,
  onChangeInstance,
  siteInfo,
  generatedBy,
}: EquipmentStatusProps) => {
  const [instanceId, setInstanceId] = useState<string | undefined>(initialInstanceId);
  const [listCollapsed, setListCollapsed] = useState(false);

  const hardwareConfig = useGetHardwareConfig(siteId);

  const equipmentFocus = useMemo(() => {
    if (instanceId && queryDisplayGroups.data) {
      return queryDisplayGroups.data?.reduce((_equipment: EquipmentInstancesDto | undefined, item) => {
        return _equipment ?? item.equipment.find(_ => _.TemperatureMetrics?.Temperature?.InstanceId === instanceId);
      }, undefined);
    }
    return undefined;
  }, [instanceId, queryDisplayGroups.data]);

  const handleChangeEquipment = (_equipment?: EquipmentInstancesDto) => {
    const temperature = _equipment?.TemperatureMetrics?.Temperature;
    setInstanceId(temperature?.InstanceId);
    if (onChangeInstance) onChangeInstance(temperature?.InstanceId);
  };

  const showList = !instanceId || !listCollapsed;
  const isPinnedList = !!instanceId && !listCollapsed;

  return (
    <HStack flex={1} w="full">
      {showList && (
        <View flex={1} maxWidth={isPinnedList ? 320 : undefined} borderRightWidth={isPinnedList ? 1 : 0}>
          <EquipmentList
            displayGroups={queryDisplayGroups}
            equipmentFocus={equipmentFocus}
            isListView={!!instanceId}
            utcOffsetInMinutes={siteInfo?.utcOffsetInMinutes}
            onChangeEquipment={handleChangeEquipment}
          />
        </View>
      )}

      {!!siteId && !!equipmentFocus && (
        <View flex={1}>
          <EquipmentChartData
            key={instanceId}
            equipmentFocus={equipmentFocus}
            siteId={siteId}
            hardwareSensors={hardwareConfig.data?.Sensors}
            listCollapsed={listCollapsed}
            onToggleList={() => setListCollapsed(v => !v)}
            siteInfo={siteInfo}
            generatedBy={generatedBy}
          />
        </View>
      )}
    </HStack>
  );
};
